import { inject, Injectable } from '@angular/core';
import { ViewConfig } from '../../../models/view-config.interface';
import { ConsigneeDeliveryNotesPaginatedService } from '@app/consignee/services/consignee-delivery-notes-paginated.service';
import { ConsigneeFoldersPaginatedService } from '@app/consignee/services/consignee-folders-paginated.service';
import { ConsigneeToursPaginatedService } from '@app/consignee/services/consignee-tours-paginated.service';
import { EventsPaginatedService } from '@app/shared/services/events-paginated.service';
import { DeliveryNoteBundlesService, ToursService } from 'api/services';
import { ConsigneeViewActionsService } from '../consignee-view-actions.service';
import { AppStore } from '@app/stores/appStore';
import { EventsStore } from '@app/stores/eventsStore';
import { SelectionStore } from '@app/stores/selectionStore';

// Import individual config factories
import { ConsigneeFoldersConfig } from './folders-config';
import { ConsigneeDeliveryNotesConfig } from './deliverynotes-config';
import { ConsigneeToursConfig } from './tours-config';
import { ConsigneeNotificationsConfig } from './notifications-config';

/**
 * Consignee View Configuration Index
 * Aggregates all consignee-specific view configurations
 */
@Injectable({
    providedIn: 'root',
})
export class ConsigneeViewConfigService {
    private appStore = inject(AppStore);
    private eventsStore = inject(EventsStore);
    private selectionStore = inject(SelectionStore);

    constructor(
        private consigneeDeliveryNotesPaginatedService: ConsigneeDeliveryNotesPaginatedService,
        private consigneeFoldersPaginatedService: ConsigneeFoldersPaginatedService,
        private consigneeToursPaginatedService: ConsigneeToursPaginatedService,
        private eventsPaginatedService: EventsPaginatedService,
        private deliveryNoteBundlesService: DeliveryNoteBundlesService,
        private toursService: ToursService,
        private consigneeViewActionsService: ConsigneeViewActionsService
    ) {}

    getViewConfigs(): Map<string, ViewConfig> {
        const configs = new Map<string, ViewConfig>();

        // Folders configuration
        configs.set(
            'consignee-folders',
            ConsigneeFoldersConfig.create({
                consigneeFoldersPaginatedService:
                    this.consigneeFoldersPaginatedService,
                deliveryNoteBundlesService: this.deliveryNoteBundlesService,
                actionsService: this.consigneeViewActionsService,
                appStore: this.appStore,
                selectionStore: this.selectionStore,
            })
        );

        // Tours configuration
        configs.set(
            'consignee-tours',
            ConsigneeToursConfig.create({
                consigneeToursPaginatedService:
                    this.consigneeToursPaginatedService,
                toursService: this.toursService,
                actionsService: this.consigneeViewActionsService,
                appStore: this.appStore,
                selectionStore: this.selectionStore,
            })
        );

        // Delivery Notes configuration
        configs.set(
            'consignee-deliverynotes',
            ConsigneeDeliveryNotesConfig.create({
                consigneeDeliveryNotesPaginatedService:
                    this.consigneeDeliveryNotesPaginatedService,
                actionsService: this.consigneeViewActionsService,
                appStore: this.appStore,
            })
        );

        // Notifications configuration
        configs.set(
            'consignee-notifications',
            ConsigneeNotificationsConfig.create({
                eventsPaginatedService: this.eventsPaginatedService,
                actionsService: this.consigneeViewActionsService,
                appStore: this.appStore,
                eventsStore: this.eventsStore,
            })
        );

        return configs;
    }
}
